import React, { useState, useRef, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Button,
  LinearProgress,
  Alert,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Paper,
  IconButton,
  Grid,
  Card,
  CardMedia,
  CardContent,
  CardActions,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions
} from '@mui/material';
import {
  CloudUpload,
  CheckCircle,
  Error as ErrorIcon,
  Close,
  Image,
  VideoLibrary,
  Description,
  Delete,
  Visibility,
  Refresh
} from '@mui/icons-material';
import { putObject, getObject, deleteObject, getSignedUrl } from '../../api/s3Api.js';
import { useAuth0 } from '@auth0/auth0-react';
import { useOrgUser } from '../../context/OrgUserProvider';

const MediaUploadTab = ({ courseId }) => {
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [uploadStatus, setUploadStatus] = useState({
    message: '',
    severity: 'info',
    open: false
  });
  const fileInputRef = useRef(null);
  const [uploadedFiles, setUploadedFiles] = useState([]);
  const [currentFile, setCurrentFile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [viewMode, setViewMode] = useState('grid');
  const [selectedMedia, setSelectedMedia] = useState(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const { getAccessTokenSilently } = useAuth0();
  const { orgUser } = useOrgUser();

  // Memoize the fetch function to prevent unnecessary re-creations
  const fetchMediaManifest = useCallback(async () => {
    try {
      setLoading(true);
      const token = await getAccessTokenSilently({
        audience: 'https://qallix-api',
      });
      const orgName = orgUser.orgName;

      try {
        const manifestResponse = await getObject(
          token,
          {
            organization: orgName,
            bucketName: 'qallix-storage',
            key: `QallixAcademy/SoftwareCourses/${courseId}/media/manifest.json`
          }
        );

        if (manifestResponse.statusCode === 200) {
          const decodedContent = atob(manifestResponse.fileContent);
          const manifest = JSON.parse(decodedContent);
          setUploadedFiles(manifest.files || []);
        }
      } catch (error) {
        setUploadedFiles([]);
      }
    } catch (error) {
      console.error('Error fetching manifest:', error);
      setUploadStatus({
        message: 'Failed to load media library',
        severity: 'error',
        open: true
      });
    } finally {
      setLoading(false);
    }
  }, [courseId, getAccessTokenSilently, orgUser.orgName]);

  // Fetch media manifest only on component mount
  useEffect(() => {
    fetchMediaManifest();
  }, [fetchMediaManifest]); // Only re-run if fetchMediaManifest changes

  const updateManifest = async (files, token, orgName) => {
    const manifest = {
      courseId,
      lastUpdated: new Date().toISOString(),
      files: files
    };

    const manifestString = JSON.stringify(manifest, null, 2);
    const base64Content = btoa(manifestString);

    const putData = {
      organization: orgName,
      putObjectItems: [
        {
          bucketName: 'qallix-storage',
          contentType: 'application/json',
          fileContent: base64Content,
          key: `QallixAcademy/SoftwareCourses/${courseId}/media/manifest.json`,
        }
      ]
    };

    await putObject(token, putData);
  };

  const getBase64 = (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => {
        const base64 = reader.result.split(',')[1];
        resolve(base64);
      };
      reader.onerror = error => reject(error);
    });
  };

  const getPresignedUrl = async (file) => {
    try {
      const token = await getAccessTokenSilently({
        audience: 'https://qallix-api',
      });
      const orgName = orgUser.orgName;

      const data = {
        organization: orgName,
        draftItems: [
          {
            bucketName: 'qallix-storage',
            key: file.key,
            expires: 3600
          }
        ]
      };

      const response = await getSignedUrl(token, data);
      
      if (response.statusCode === 200 && response.data && response.data.length > 0) {
        return response.data[0];
      }
      throw new Error('Failed to generate presigned URL');
    } catch (error) {
      console.error('Error generating presigned URL:', error);
      return null;
    }
  };

  const handleViewFile = async (file) => {
    const url = await getPresignedUrl(file);
    if (url) {
      window.open(url, '_blank');
    } else {
      setUploadStatus({
        message: 'Failed to generate access URL',
        severity: 'error',
        open: true
      });
    }
  };

  const ImagePreview = ({ file }) => {
    const [imageUrl, setImageUrl] = useState('');

    useEffect(() => {
      const loadImage = async () => {
        const url = await getPresignedUrl(file);
        setImageUrl(url || '');
      };
      
      if (file.type.startsWith('image/')) {
        loadImage();
      }
    }, [file]);

    if (!imageUrl) {
      return (
        <Box sx={{ height: 140, display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: '#f5f5f5' }}>
          {getFileIcon(file.type)}
          <Typography variant="body2" sx={{ ml: 1 }}>
            {file.type.split('/')[1]?.toUpperCase()}
          </Typography>
        </Box>
      );
    }

    return (
      <CardMedia
        component="img"
        height="140"
        image={imageUrl}
        alt={file.name}
        onError={(e) => {
          e.target.style.display = 'none';
        }}
      />
    );
  };

  const handleFileSelect = (event) => {
    const files = Array.from(event.target.files);
    if (files.length === 0) return;
    handleFileUpload(files);
  };

  const handleFileUpload = async (files) => {
    setUploading(true);
    setProgress(0);
    setUploadStatus({
      message: `Uploading ${files.length} file(s)...`,
      severity: 'info',
      open: true
    });

    try {
      const token = await getAccessTokenSilently({
        audience: 'https://qallix-api',
      });
      const orgName = orgUser.orgName;

      const newFiles = [...uploadedFiles];

      for (let i = 0; i < files.length; i++) {
        const file = files[i];
        setCurrentFile(file.name);
        const base64Data = await getBase64(file);
        const putData = {
          organization: orgName,
          putObjectItems: [
            {
              bucketName: 'qallix-storage',
              contentType: file.type,
              fileContent: base64Data,
              key: `QallixAcademy/SoftwareCourses/${courseId}/media/${file.name}`,
            }
          ]
        };
        await putObject(token, putData);

        newFiles.push({
          name: file.name,
          size: file.size,
          type: file.type,
          uploadedAt: new Date().toISOString(),
          key: `QallixAcademy/SoftwareCourses/${courseId}/media/${file.name}`
        });

        setProgress(((i + 1) / files.length) * 100);
      }

      await updateManifest(newFiles, token, orgName);
      setUploadedFiles(newFiles);

      setUploadStatus({
        message: `${files.length} file(s) uploaded successfully!`,
        severity: 'success',
        open: true
      });
    } catch (error) {
      console.error('Upload error:', error);
      setUploadStatus({
        message: error.toString() || 'Failed to upload file(s)',
        severity: 'error',
        open: true
      });
    } finally {
      setUploading(false);
      setProgress(0);
      setCurrentFile(null);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const handleDeleteMedia = async (file) => {
    try {
      const token = await getAccessTokenSilently({
        audience: 'https://qallix-api',
      });
      const orgName = orgUser.orgName;

      const deleteData = {
        organization: orgName,
        bucketName: 'qallix-storage',
        key: file.key
      };

      await deleteObject(token, deleteData);

      const updatedFiles = uploadedFiles.filter(f => f.key !== file.key);
      await updateManifest(updatedFiles, token, orgName);
      setUploadedFiles(updatedFiles);

      setUploadStatus({
        message: 'File deleted successfully',
        severity: 'success',
        open: true
      });
    } catch (error) {
      console.error('Delete error:', error);
      setUploadStatus({
        message: 'Failed to delete file',
        severity: 'error',
        open: true
      });
    } finally {
      setDeleteDialogOpen(false);
      setSelectedMedia(null);
    }
  };

  const getFileIcon = (type) => {
    if (type.startsWith('image/')) return <Image color="primary" />;
    if (type.startsWith('video/')) return <VideoLibrary color="primary" />;
    return <Description color="primary" />;
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    const files = Array.from(e.dataTransfer.files);
    if (files.length > 0) {
      handleFileUpload(files);
    }
  };

  const handleCloseAlert = () => {
    setUploadStatus({ ...uploadStatus, open: false });
  };

  if (loading) {
    return (
      <Box sx={{ p: 3, textAlign: 'center' }}>
        <Typography>Loading media library...</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h5" fontWeight="bold" color="#2c3e50" fontFamily={"'Satoshi', sans-serif"}>
          Media Library
        </Typography>
        <Box>
          <Button
            variant="outlined"
            startIcon={<Refresh />}
            onClick={fetchMediaManifest}
            sx={{
              backgroundColor: '#372e5e',
              color: 'white',
              '&:hover': { backgroundColor: '#282241' },
              fontFamily: "'Satoshi', sans-serif",
              mr: 1
            }}
          >
            Refresh
          </Button>
          <Button
            variant={viewMode === 'grid' ? 'contained' : 'outlined'}
            onClick={() => setViewMode('grid')}
            sx={{
              backgroundColor: viewMode === 'list' ? '#372e5e' : 'transparent',
              color: viewMode === 'list' ? 'white' : '#372e5e',
              borderColor: viewMode === 'list' ? '#372e5e' : '#ccc',
              '&:hover': { backgroundColor: '#282241', color: 'white' },
              fontFamily: "'Satoshi', sans-serif",
              mr: 1
            }}
          >
            Grid
          </Button>
          <Button
            variant={viewMode === 'list' ? 'contained' : 'outlined'}
            onClick={() => setViewMode('list')}
            sx={{
              backgroundColor: viewMode === 'grid' ? '#372e5e' : 'transparent',
              color: viewMode === 'grid' ? 'white' : '#372e5e',
              borderColor: viewMode === 'grid' ? '#372e5e' : '#ccc',
              '&:hover': { backgroundColor: '#282241', color: 'white' },
              fontFamily: "'Satoshi', sans-serif"
            }}
          >
            List
          </Button>
        </Box>
      </Box>

      <Typography variant="body1" color="textSecondary" gutterBottom fontFamily={"'Satoshi', sans-serif"}>
        Upload and manage images, videos, documents, and other media files for your course.
      </Typography>

      <Paper
        variant="outlined"
        onDragOver={handleDragOver}
        onDrop={handleDrop}
        sx={{
          p: 4,
          textAlign: 'center',
          border: '2px dashed #ccc',
          backgroundColor: '#fafafa',
          cursor: 'pointer',
          mt: 2,
          mb: 4,
          '&:hover': {
            borderColor: '#372e5e',
            backgroundColor: '#f0f0f0'
          }
        }}
        onClick={() => fileInputRef.current?.click()}
      >
        <CloudUpload sx={{ fontSize: 48, color: '#372e5e', mb: 1 }} />
        <Typography variant="h6" gutterBottom fontFamily={"'Satoshi', sans-serif"}>
          Drag and drop files here
        </Typography>
        <Typography variant="body2" color="textSecondary" gutterBottom fontFamily={"'Satoshi', sans-serif"}>
          or
        </Typography>
        <Button
          variant="contained"
          component="span"
          sx={{
            backgroundColor: '#372e5e',
            '&:hover': { backgroundColor: '#282241' }
          }}
        >
          Browse Files
        </Button>
        <input
          type="file"
          ref={fileInputRef}
          onChange={handleFileSelect}
          multiple
          style={{ display: 'none' }}
        />
      </Paper>

      {uploading && (
        <Box sx={{ mt: 3, mb: 3 }}>
          <Typography variant="body2" gutterBottom>
            Uploading: {currentFile}
          </Typography>
          <LinearProgress
            variant="determinate"
            value={progress}
            sx={{ mb: 1, height: 8, borderRadius: 4 }}
          />
          <Typography variant="body2" align="center">
            {Math.round(progress)}% complete
          </Typography>
        </Box>
      )}

      {uploadStatus.open && (
        <Alert
          severity={uploadStatus.severity}
          sx={{ mt: 2, mb: 2 }}
          action={
            <IconButton onClick={handleCloseAlert}>
              <Close fontSize="inherit" />
            </IconButton>
          }
        >
          {uploadStatus.message}
        </Alert>
      )}

      {uploadedFiles.length > 0 ? (
        viewMode === 'grid' ? (
          <Grid container spacing={2}>
            {uploadedFiles.map((file, index) => (
              <Grid item xs={12} sm={6} md={4} lg={3} key={index}>
                <Card>
                  {file.type.startsWith('image/') ? (
                    <ImagePreview file={file} />
                  ) : (
                    <Box sx={{ height: 140, display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: '#f5f5f5' }}>
                      {getFileIcon(file.type)}
                      <Typography variant="body2" sx={{ ml: 1 }}>
                        {file.type.split('/')[1]?.toUpperCase()}
                      </Typography>
                    </Box>
                  )}
                  <CardContent>
                    <Typography variant="body2" noWrap title={file.name}>
                      {file.name}
                    </Typography>
                    <Typography variant="caption" color="textSecondary">
                      {formatFileSize(file.size)}
                    </Typography>
                  </CardContent>
                  <CardActions>
                    <IconButton 
                      size="small" 
                      onClick={() => handleViewFile(file)}
                      title="View file"
                    >
                      <Visibility />
                    </IconButton>
                    <IconButton
                      size="small"
                      color="error"
                      onClick={() => {
                        setSelectedMedia(file);
                        setDeleteDialogOpen(true);
                      }}
                      title="Delete file"
                    >
                      <Delete />
                    </IconButton>
                  </CardActions>
                </Card>
              </Grid>
            ))}
          </Grid>
        ) : (
          <List>
            {uploadedFiles.map((file, index) => (
              <ListItem
                key={index}
                sx={{
                  border: '1px solid #e0e0e0',
                  borderRadius: 1,
                  mb: 1,
                  backgroundColor: '#fafafa'
                }}
              >
                <ListItemIcon>
                  {getFileIcon(file.type)}
                </ListItemIcon>
                <ListItemText
                  primary={file.name}
                  secondary={
                    <Box sx={{ display: 'flex', flexDirection: 'column', mt: 0.5 }}>
                      <Typography variant="caption">
                        {formatFileSize(file.size)} • {file.type.split('/')[1]}
                      </Typography>
                      <Typography variant="caption" color="textSecondary">
                        Uploaded: {new Date(file.uploadedAt).toLocaleString()}
                      </Typography>
                    </Box>
                  }
                />
                <IconButton 
                  onClick={() => handleViewFile(file)}
                  title="View file"
                >
                  <Visibility />
                </IconButton>
                <IconButton
                  color="error"
                  onClick={() => {
                    setSelectedMedia(file);
                    setDeleteDialogOpen(true);
                  }}
                  title="Delete file"
                >
                  <Delete />
                </IconButton>
              </ListItem>
            ))}
          </List>
        )
      ) : (
        <Box sx={{ textAlign: 'center', py: 4 }}>
          <Typography variant="body1" color="textSecondary">
            No media files uploaded yet. Upload some files to get started.
          </Typography>
        </Box>
      )}

      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <DialogTitle>Delete Media</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete "{selectedMedia?.name}"? This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
          <Button
            onClick={() => handleDeleteMedia(selectedMedia)}
            color="error"
            variant="contained"
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default MediaUploadTab;